import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Phone, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const OPCOES = [
  { key: 'em_contacto', label: 'Em Contacto' },
  { key: 'reagendada', label: 'Reagendada' },
  { key: 'incontactavel', label: 'Incontactável' },
  { key: 'sem_interesse', label: 'Sem Interesse' },
  { key: 'invalida', label: 'Inválida' },
];

const Row = ({ l, onDone }) => {
  const [st, setSt] = useState('');
  const [nota, setNota] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (st === 'incontactavel' && !nota.trim()) { toast.error('Nota obrigatória para Incontactável'); return; }
    setBusy(true);
    try {
      await axios.put(`${API_URL}/leads/${l.id}/status`, { status: st, notes: nota.trim() || undefined });
      onDone(l.id);
    } catch (e) {
      toast.error((typeof e.response?.data?.detail === 'string' && e.response.data.detail) || 'Erro');
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`nova72-row-${l.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-800">{l.client_name || 'Cliente'}</p>
          {l.client_phone && <a href={`tel:${l.client_phone}`} className="text-sm text-slate-600 inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{l.client_phone}</a>}
        </div>
        <span className="text-[11px] font-bold text-rose-700 bg-rose-100 rounded-full px-2 py-0.5 whitespace-nowrap">Nova há {Math.floor(l.horas / 24)}d {l.horas % 24}h</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {OPCOES.map((o) => (
          <button key={o.key} type="button" onClick={() => setSt(o.key)} data-testid={`nova72-opt-${o.key}-${l.id}`}
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${st === o.key ? 'bg-[#2D3E6F] text-white border-[#2D3E6F]' : 'bg-white border-slate-200 text-slate-600 hover:border-[#2D3E6F]'}`}>{o.label}</button>
        ))}
      </div>
      {st && (
        <div className="flex gap-2">
          <input value={nota} onChange={(e) => setNota(e.target.value)} maxLength={500} data-testid={`nova72-note-${l.id}`}
            placeholder={st === 'incontactavel' ? 'Nota obrigatória (ex.: liguei 3x)' : 'Nota (opcional)'}
            className="flex-1 border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
          <Button size="sm" disabled={busy} onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`nova72-save-${l.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </div>
      )}
    </div>
  );
};

export const LeadsNova72hBlockingPopup = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    if (!user) return;
    try { const { data } = await axios.get(`${API_URL}/leads/novas-72h`, { params: { mine: true } }); setRows(Array.isArray(data) ? data : []); } catch { /* silent */ }
  }, [user]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  if (!user || !rows.length) return null;
  return (
    <div className="fixed inset-0 z-[9996] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="nova72-popup">
      <div className="w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl overflow-hidden bg-white shadow-2xl animate-in zoom-in-95 duration-300">
        <div className="bg-rose-600 px-6 py-4 text-white flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-white text-rose-600 flex items-center justify-center"><AlertTriangle className="w-5 h-5" /></span>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Verificação obrigatória</p>
            <p className="font-bold" data-testid="nova72-count">{rows.length} lead{rows.length > 1 ? 's' : ''} em "Nova" há mais de 72h</p>
          </div>
        </div>
        <p className="px-6 pt-4 text-xs text-slate-500">Atualize o estado de cada lead para continuar a usar o CRM. O gestor acompanha estas leads.</p>
        <div className="p-6 pt-3 space-y-3 overflow-y-auto">
          {rows.map((l) => <Row key={l.id} l={l} onDone={(id) => setRows((r) => r.filter((x) => x.id !== id))} />)}
        </div>
      </div>
    </div>
  );
};
